import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { environment } from '../environments/environment';
import { callApi, clearComponentState, saveComponentState } from '../state-files/action';
import { ApiFailureEntry, ApiState } from '../state-files/reducer';
import { selectApiFailure, selectComponentState, selectIsComponentStateFresh } from '../state-files/selectors';
import { awaitApiStoreResult } from './api-store-request';
import { AuthSessionWatchdogService } from './auth-session-watchdog.service';
import { DEFAULT_REQUEST_TIMEOUT_MS, withRequestTimeout } from './request-timeout';
import { UserService } from './user.service';

export interface AppState {
  api: ApiState;
}

/**
 * Injectable service for making authenticated API requests, caching endpoint data in the store, and persisting component UI state.
 * @service
 * @providedIn root
 * @example
 *   constructor(private reduxApiHandler: ReduxApiHandlerService) {}
 * @remarks
 *   Uses HttpClient to call backend endpoints with token validation and HMAC headers, and Store to read/write cached API data and component state.
 * @method callApi
 *   Performs an authenticated HTTP request for the given method, endpoint, and payload.
 * @method fetchWithCache
 *   Returns cached endpoint data when available, otherwise dispatches a GET request and waits for the store update.
 * @method forceRefreshAll
 *   Re-fetches all tracked endpoints by dispatching GET requests for each one.
 * @method saveState
 *   Persists a component's filters and grid data into the store.
 * @method clearState
 *   Removes persisted state for the specified component key.
 * @method loadState
 *   Loads persisted state for the specified component key.
 * @method isStateFresh
 *   Determines whether the persisted state for the specified component key is fresh.
 */
@Injectable({ providedIn: 'root', })
export class ReduxApiHandlerService {
  private apiUrl = environment.API_BASE_URL;
  private trackedEndpoints: string[] = [];

  /**
   * @param http - HttpClient used for every request issued through this handler.
   * @param userService - Token validation and HMAC signing for authenticated requests.
   * @param store - NgRx store holding cached endpoint data and persisted component state.
   * @param sessionWatchdog - Told about every successful authenticated response, which is the proof that disarms the stale-session fallback.
   */
  constructor(
    private http: HttpClient,
    private userService: UserService,
    private store: Store<AppState>,
    private sessionWatchdog: AuthSessionWatchdogService
  ) { }

  /**
   * Sends an authenticated HTTP request to the configured API endpoint using the specified method.
   * Automatically generates the required HMAC header, applies bearer authentication, and returns the full HTTP response as an Observable.
   * A 2xx response is also reported to the session watchdog (side effect only; the response is passed through unchanged).
   * Fails with a 408 HttpErrorResponse after DEFAULT_REQUEST_TIMEOUT_MS, so the `callApi` effect always
   * settles (success or `callApiFailure`) and store-backed callers can never wait forever.
   * @example
   * allApi('POST', '/users', { name: 'John' })
   * Observable<HttpResponse<any>>
   * @param {string} method - HTTP method to use for the request.
   * @param {string} endpoint - API endpoint path to append to the base URL.
   * @param {any} payload - Request body for POST/PUT requests, or query parameters for GET requests.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the full HTTP response from the API call.
   **/
  callApi(method: string, endpoint: string, payload: any = {}): Observable<HttpResponse<any>> {
    return this.userService.ensureValidToken().pipe(
      switchMap((token: string) => {
        // Use generateHmacWithoutPayload for empty payloads, otherwise use generateHmac
        const isEmptyPayload = !payload || (Object.keys(payload).length === 0 && payload.constructor === Object);
        const hmac = isEmptyPayload
          ? this.userService.generateHmacWithoutPayload("")
          : this.userService.generateHmac(payload, "");

        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Payload-HMAC': hmac,
        });

        const options = { headers, observe: 'response' as const };

        switch (method.toUpperCase()) {
          case 'POST':
            return this.http.post<any>(`${this.apiUrl}${endpoint}`, payload, options);
          case 'PUT':
            return this.http.put<any>(`${this.apiUrl}${endpoint}`, payload, options);
          case 'DELETE':
            return this.http.delete<any>(`${this.apiUrl}${endpoint}`, options);
          default:
            {
              const query = new URLSearchParams(payload).toString();
              return this.http.get<any>(`${this.apiUrl}${endpoint}${query ? '?' + query : ''}`, options);
            }
        }
      }),
      withRequestTimeout<HttpResponse<any>>(DEFAULT_REQUEST_TIMEOUT_MS, `${this.apiUrl}${endpoint}`),
      // The server accepting the bearer token proves the session behind the guard's
      // sentinel is real — same contract as ApiService.
      this.sessionWatchdog.confirmOnSuccess()
    );
  }

  /**
  * Retrieves cached API data for an endpoint or dispatches a GET request and waits for the result when no cache is available.
  * @example
  * etchWithCache('/users', {})
  * // Observable containing cached users data or fresh API response
  * @param {string} endpoint - API endpoint used as the cache key and request target.
  * @param {any} payload - Optional request payload passed with the API call.
  * @param {boolean} forceRefresh - When true, ignores cached data and fetches fresh data.
  * @returns {Observable<T>} An observable that emits the cached or newly fetched response data,
  *   or errors when the dispatched request records a `callApiFailure` for the endpoint.
  **/
  fetchWithCache<T>(endpoint: string, payload: any = {}, forceRefresh = false): Observable<T> {
    if (!this.trackedEndpoints.includes(endpoint)) {
      this.trackedEndpoints.push(endpoint);
    }

    return this.store.select(state => state.api.data[endpoint]).pipe(
      take(1),
      switchMap(cachedData => {
        if (cachedData && !forceRefresh) {
          return of(cachedData) as Observable<T>;
        } else {
          let baseline: ApiFailureEntry | undefined;
          this.store.select(selectApiFailure(endpoint)).pipe(take(1)).subscribe((entry) => (baseline = entry));
          // On a forced refresh the old value is still in the store; skip it so the caller
          // receives the refreshed response rather than the stale one.
          const staleData = forceRefresh ? cachedData : undefined;
          return new Observable<T>((subscriber) => {
            // Subscribe before dispatching so a failure recorded during the dispatch is not missed.
            const outcome = awaitApiStoreResult(this.store, endpoint, baseline, staleData).subscribe(subscriber);
            this.store.dispatch(callApi({ method: 'GET', endpoint, payload, key: endpoint }));
            return outcome;
          });
        }
      })
    );
  }

  forceRefreshAll(): void {
    this.trackedEndpoints.forEach(endpoint => {
      this.store.dispatch(callApi({ method: 'GET', endpoint, payload: {}, key: endpoint }));
    });
  }

  saveState(componentKey: string, filters: any, gridData: any[]) {
    const state = { filters, gridData };
    this.store.dispatch(saveComponentState({ componentKey, state }));
  }

  clearState(componentKey: string) {
    this.store.dispatch(clearComponentState({ componentKey }));
  }

  loadState(componentKey: string): Promise<any> {
    return this.store.select(selectComponentState(componentKey)).pipe(take(1)).toPromise();
  }

  isStateFresh(componentKey: string): Promise<boolean> {
    return this.store
      .select(selectIsComponentStateFresh(componentKey))
      .pipe(
        take(1),
        map(fresh => fresh ?? false)
      )
      .toPromise() as Promise<boolean>;
  }

}
